/**
 * 戰情同步異常清單（streams DLQ）— AC-I-6。
 *
 * 對應 BE：
 *   - GET  /admin/streams/dlq?status=        戰情事件 DLQ 清單
 *   - POST /admin/streams/dlq/:id/resend     手動重送（需 streams.dlq.manage）
 *   - POST /admin/streams/dlq/:id/ignore     標記忽略（需 streams.dlq.manage）
 *
 * Notes：
 *   - 30s 自動重抓（同步異常為運維核心指標）
 *   - status 篩選：open（未處理）/ resent / ignored / all
 *   - SYNC-002：open 筆數 > 50 時顯示警報橫幅（對齊 BE DLQ open>50/小時觸發
 *     SYNC-002 → dispatchP1 之門檻）
 *   - F-2 同步狀態看板延伸；CMS mockup 22-G2-bi-sync
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Button } from '../components/Button';
import { Pill, type PillTone } from '../components/Pill';
import {
  ignoreStreamsDlq,
  listStreamsDlq,
  resendStreamsDlq,
  type StreamsDlqEntry,
  type StreamsDlqStatus,
} from '../lib/api-client';

/** SYNC-002 警報門檻：open DLQ 筆數超過即示警（對齊 BE 觸發條件）。 */
const SYNC_002_THRESHOLD = 50;

const STATUS_TABS: { value: StreamsDlqStatus; label: string }[] = [
  { value: 'open', label: '未處理' },
  { value: 'resent', label: '已重送' },
  { value: 'ignored', label: '已忽略' },
  { value: 'all', label: '全部' },
];

const STATUS_TONE: Record<string, PillTone> = {
  open: 'danger',
  resent: 'success',
  ignored: 'neutral',
};

function statusLabel(s: string): string {
  if (s === 'open') return '未處理';
  if (s === 'resent') return '已重送';
  if (s === 'ignored') return '已忽略';
  return s;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

function summariseResponse(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') return value;
  try {
    const text = JSON.stringify(value);
    return text.length > 80 ? `${text.slice(0, 80)}…` : text;
  } catch {
    return '—';
  }
}

export function StreamsDlqListPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StreamsDlqStatus>('open');

  const dlqQuery = useQuery({
    queryKey: ['streams.dlq.list', status],
    queryFn: () => listStreamsDlq(status),
    refetchInterval: 30_000,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['streams.dlq.list'] });
  };

  const resendMutation = useMutation({
    mutationFn: (id: string) => resendStreamsDlq(id),
    onSuccess: invalidate,
  });
  const ignoreMutation = useMutation({
    mutationFn: (id: string) => ignoreStreamsDlq(id),
    onSuccess: invalidate,
  });

  const items: StreamsDlqEntry[] = dlqQuery.data?.items ?? [];

  /** 各 status 不在 BE 回應內時的整體 open 數——以目前清單估算（open tab 最準）。 */
  const openCount = useMemo(() => items.filter((it) => it.status === 'open').length, [items]);
  const sync002Tripped = status === 'open' && openCount > SYNC_002_THRESHOLD;

  const busy = resendMutation.isPending || ignoreMutation.isPending;

  const handleResend = (entry: StreamsDlqEntry): void => {
    if (
      window.confirm(
        `確定要手動重送此 DLQ 事件？\n事件類型：${entry.event_type}\nProducer：${entry.producer_id}\n重試次數：${entry.retry_count}\n\n操作會寫入 audit log。`,
      )
    ) {
      resendMutation.mutate(entry.id);
    }
  };

  const handleIgnore = (entry: StreamsDlqEntry): void => {
    if (
      window.confirm(
        `確定要將此 DLQ 事件標記為「已忽略」？\n事件類型：${entry.event_type}\nProducer：${entry.producer_id}\n\n忽略後不再重送；操作會寫入 audit log。`,
      )
    ) {
      ignoreMutation.mutate(entry.id);
    }
  };

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">戰情同步異常（DLQ／I-6）</h1>
        <div className="page-head__actions">
          <Button
            variant="secondary"
            onClick={() => dlqQuery.refetch()}
            disabled={dlqQuery.isFetching}
          >
            {dlqQuery.isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">
        戰情事件推送失敗後進入 DLQ（AC-I-6）；本頁列出同步異常並可手動重送或忽略。每 30 秒自動更新。
      </p>

      {/* SYNC-002 警報橫幅 */}
      {sync002Tripped && (
        <div
          className="error-banner"
          data-testid="streams-dlq-sync002"
          style={{ marginBottom: 16 }}
        >
          ⚠ SYNC-002：未處理 DLQ 筆數（{openCount}）已超過警報門檻 {SYNC_002_THRESHOLD}
          ；請儘速處置同步異常。
        </div>
      )}

      {/* 狀態篩選頁籤 */}
      <div style={{ display: 'flex', gap: 8, margin: '0 0 16px' }}>
        {STATUS_TABS.map((tab) => (
          <Button
            key={tab.value}
            variant={status === tab.value ? 'primary' : 'secondary'}
            onClick={() => setStatus(tab.value)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {dlqQuery.isLoading && <div>載入中…</div>}
      {dlqQuery.error && <div className="error-banner">{(dlqQuery.error as Error).message}</div>}
      {resendMutation.error && (
        <div className="error-banner">重送失敗：{(resendMutation.error as Error).message}</div>
      )}
      {ignoreMutation.error && (
        <div className="error-banner">忽略失敗：{(ignoreMutation.error as Error).message}</div>
      )}

      {dlqQuery.data && (
        <div className="table-card" data-testid="streams-dlq-table">
          <table>
            <thead>
              <tr>
                <th>事件類型</th>
                <th>Producer</th>
                <th>失敗類別</th>
                <th style={{ textAlign: 'right' }}>重試</th>
                <th>首次失敗</th>
                <th>最後失敗</th>
                <th>最後回應</th>
                <th>狀態</th>
                <th style={{ width: 180 }}>動作</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={9} className="empty-cell">
                    目前無符合條件之 DLQ 項目
                  </td>
                </tr>
              )}
              {items.map((entry) => (
                <tr key={entry.id}>
                  <td style={{ fontSize: 13, fontWeight: 600 }}>{entry.event_type}</td>
                  <td style={{ fontSize: 13 }}>{entry.producer_id}</td>
                  <td style={{ fontSize: 13 }}>{entry.failure_class}</td>
                  <td style={{ textAlign: 'right' }}>{entry.retry_count}</td>
                  <td style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                    {formatDateTime(entry.first_failed_at)}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                    {formatDateTime(entry.last_failed_at)}
                  </td>
                  <td
                    style={{ fontSize: 12, color: 'var(--c-text-muted)', maxWidth: 240 }}
                    title={summariseResponse(entry.last_response)}
                  >
                    {summariseResponse(entry.last_response)}
                  </td>
                  <td>
                    <Pill tone={STATUS_TONE[entry.status] ?? 'neutral'} dot={false}>
                      {statusLabel(entry.status)}
                    </Pill>
                  </td>
                  <td>
                    {entry.status === 'open' ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button
                          variant="secondary"
                          onClick={() => handleResend(entry)}
                          disabled={busy}
                        >
                          重送
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => handleIgnore(entry)}
                          disabled={busy}
                        >
                          忽略
                        </Button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
